import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { statsApi } from '../services/api';

/*
 * 통계 > 여행 비용
 *
 * 여행은 따로 만들지 않는다. 거래 메모를 "(여행) 퍼스: 항공권" 처럼 적으면
 * 서버가 "퍼스" 라는 여행으로 묶어 준다. 여기서 사람이 덧붙이는 건 보이는
 * 이름과 여행 기간뿐이다 — 경비를 적는 습관은 지금 그대로 두면 된다.
 */

const Wrap = styled.div`
  padding: 4px 0 24px;
`;

const Head = styled.div`
  margin-bottom: 16px;

  h3 {
    margin: 0 0 6px;
    font-size: 1.35rem;
    color: #333;
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #888;
  line-height: 1.5;

  code {
    background: #f3f4f6;
    border-radius: 4px;
    padding: 1px 6px;
    font-family: inherit;
    color: #555;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
`;

const Card = styled.button`
  text-align: left;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  padding: 18px 18px 16px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #b9d6ff;
    box-shadow: 0 6px 14px rgba(0, 123, 255, 0.12);
  }
`;

const CardTitle = styled.div`
  font-size: 1.08rem;
  font-weight: 700;
  color: #222;
`;

const Period = styled.div`
  margin-top: 3px;
  font-size: 0.85rem;
  color: ${(p) => (p.$muted ? '#bbb' : '#777')};
`;

const Big = styled.div`
  margin-top: 14px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #007bff;
  font-variant-numeric: tabular-nums;
`;

const Meta = styled.div`
  margin-top: 2px;
  font-size: 0.8rem;
  color: #999;
`;

const Back = styled.button`
  background: none;
  border: none;
  padding: 4px 0;
  margin-bottom: 10px;
  color: #007bff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;

const DetailHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;

  h3 {
    margin: 0;
    font-size: 1.4rem;
    color: #222;
  }
`;

const LinkBtn = styled.button`
  background: none;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 6px 12px;
  color: #555;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover { border-color: #007bff; color: #007bff; }
`;

const Summary = styled.div`
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f5f9ff;
  margin-bottom: 16px;

  div { font-size: 0.82rem; color: #777; }
  strong {
    display: block;
    margin-top: 2px;
    font-size: 1.2rem;
    color: #222;
    font-variant-numeric: tabular-nums;
  }
  .total strong { color: #007bff; font-size: 1.45rem; }
  .refund strong { color: #2e7d32; }
`;

const EditBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  padding: 14px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  margin-bottom: 16px;

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.8rem;
    color: #666;
  }
  input {
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.95rem;
  }
  select {
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.95rem;
    background: #fff;
    max-width: 100%;
  }
  .title { flex: 1 1 200px; }
`;

const HeadButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

/* 합쳐 들어온 여행. 잘못 합쳤을 때 바로 되돌릴 수 있게 옆에 떼어 내기를 둔다. */
const MergedNote = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  margin: -4px 0 14px;
  font-size: 0.84rem;
  color: #888;

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 4px 3px 10px;
    border: 1px solid #e3e3e3;
    border-radius: 999px;
    color: #555;
    background: #fafafa;
  }
  .chip button {
    border: none;
    background: none;
    padding: 2px 8px;
    border-radius: 999px;
    color: #999;
    font-size: 0.78rem;
    cursor: pointer;
  }
  .chip button:hover { background: #fdeaea; color: #c62828; }
`;

const SaveBtn = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: 6px;
  background: #007bff;
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.55; cursor: default; }
`;

const List = styled.div`
  border-top: 1px solid #eee;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 74px 1fr auto;
  gap: 4px 12px;
  align-items: baseline;
  padding: 11px 4px;
  border-bottom: 1px solid #f0f0f0;
  opacity: ${(p) => (p.$excluded ? 0.5 : 1)};

  .date {
    font-size: 0.82rem;
    color: #999;
    font-variant-numeric: tabular-nums;
  }
  .label {
    font-size: 0.97rem;
    color: #333;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .acct {
    grid-column: 2;
    font-size: 0.76rem;
    color: #aaa;
  }
  .amt {
    grid-row: 1;
    grid-column: 3;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${(p) => (p.$refund ? '#2e7d32' : '#222')};
    white-space: nowrap;
  }
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: #999;
  line-height: 1.7;
  border: 2px dashed #e5e5e5;
  border-radius: 12px;
`;

const ErrorText = styled.p`
  color: #c62828;
  font-size: 0.9rem;
`;

const won = (v) => `₩${Math.round(v || 0).toLocaleString()}`;

const parseYmd = (s) => {
  const [y, m, d] = (s || '').split('-').map(Number);
  return { y, m, d };
};

/** "26년 11월 19일~11월 28일". 해가 바뀌면 끝쪽에도 연도를 붙인다. */
function formatPeriod(start, end) {
  if (!start && !end) return '';
  const short = ({ y, m, d }, withYear) => `${withYear ? `${String(y).slice(2)}년 ` : ''}${m}월 ${d}일`;
  if (start && end) {
    const a = parseYmd(start);
    const b = parseYmd(end);
    return `${short(a, true)}~${short(b, a.y !== b.y)}`;
  }
  return start ? `${short(parseYmd(start), true)}~` : `~${short(parseYmd(end), true)}`;
}

/** "9박 10일" */
function formatNights(start, end) {
  if (!start || !end) return '';
  const days = Math.round((new Date(end) - new Date(start)) / 86400000);
  if (days < 0) return '';
  return days === 0 ? '당일' : `${days}박 ${days + 1}일`;
}

/** "26.04.28" — 예약은 몇 달 전에 하니 연도까지 짧게. */
const shortDate = (s) => (s ? `${s.slice(2, 4)}.${s.slice(5, 7)}.${s.slice(8, 10)}` : '');

function TravelCosts() {
  const [trips, setTrips] = useState(null);
  const [detail, setDetail] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // 다른 여행에 합치기. 메모 적는 방식이 해마다 달라(예: "여행-(하영)푸꾸옥정산"
  // 과 "여행-(하영)푸꾸옥항공권숙소") 한 여행이 둘로 갈라져 나올 때 쓴다.
  const [merging, setMerging] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeBusy, setMergeBusy] = useState(false);

  const loadTrips = useCallback(async () => {
    try {
      const res = await statsApi.getTrips();
      setTrips(res.data || []);
      setError(null);
    } catch (e) {
      setTrips([]);
      setError('여행 목록을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const openTrip = async (key) => {
    setOpenKey(key);
    setDetail(null);
    setEditing(false);
    setMerging(false);
    setFormError(null);
    try {
      const res = await statsApi.getTrip(key);
      setDetail(res.data);
    } catch (e) {
      setError('여행 경비를 불러오지 못했습니다.');
      setOpenKey(null);
    }
  };

  const backToList = () => {
    setOpenKey(null);
    setDetail(null);
    setEditing(false);
    setMerging(false);
    loadTrips(); // 이름·기간을 고쳤거나 합쳤을 수 있다
  };

  const startMerge = () => {
    setMergeTarget('');
    setFormError(null);
    setEditing(false);
    setMerging(true);
  };

  const doMerge = async () => {
    const target = (trips || []).find((t) => t.key === mergeTarget);
    if (!target) return;
    if (!window.confirm(`「${detail.title}」을(를) 「${target.title}」에 합칠까요?\n거래 기록은 그대로 두고 보는 곳만 하나로 묶습니다.`)) return;
    setMergeBusy(true);
    setFormError(null);
    try {
      const res = await statsApi.mergeTrip(detail.key, target.key);
      // 합친 뒤에는 합쳐 들어간 여행 쪽을 보여준다
      setOpenKey(res.data.key);
      setDetail(res.data);
      setMerging(false);
      loadTrips();
    } catch (e) {
      setFormError(e.response?.data?.detail || '합치지 못했습니다.');
    } finally {
      setMergeBusy(false);
    }
  };

  const doUnmerge = async (sourceKey, sourceTitle) => {
    if (!window.confirm(`「${sourceTitle}」을(를) 떼어 내 따로 볼까요?`)) return;
    setMergeBusy(true);
    try {
      const res = await statsApi.unmergeTrip(sourceKey);
      setDetail(res.data);
      loadTrips();
    } catch (e) {
      setFormError(e.response?.data?.detail || '떼어 내지 못했습니다.');
    } finally {
      setMergeBusy(false);
    }
  };

  const startEdit = () => {
    setForm({
      title: detail.title || '',
      start_date: detail.start_date || '',
      end_date: detail.end_date || '',
    });
    setFormError(null);
    setMerging(false);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const res = await statsApi.updateTrip(detail.key, form);
      setDetail(res.data);
      setEditing(false);
    } catch (e) {
      setFormError(e.response?.data?.detail || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- 여행 하나 ----------
  if (openKey) {
    if (!detail) {
      return (
        <Wrap>
          <Back onClick={backToList}>← 여행 목록</Back>
          <Hint>불러오는 중…</Hint>
        </Wrap>
      );
    }

    const period = formatPeriod(detail.start_date, detail.end_date);
    const nights = formatNights(detail.start_date, detail.end_date);
    const hasExcluded = detail.items.some((i) => !i.counted);

    return (
      <Wrap>
        <Back onClick={backToList}>← 여행 목록</Back>

        <DetailHead>
          <div>
            <h3>{detail.title}</h3>
            <Period $muted={!period}>
              {period ? `${period}${nights ? ` · ${nights}` : ''}` : '기간 미정'}
            </Period>
          </div>
          {!editing && !merging && (
            <HeadButtons>
              <LinkBtn onClick={startEdit}>이름·기간 수정</LinkBtn>
              {(trips || []).length > 1 && (
                <LinkBtn onClick={startMerge}>다른 여행에 합치기</LinkBtn>
              )}
            </HeadButtons>
          )}
        </DetailHead>

        {merging && (
          <EditBox>
            <label className="title">
              이 여행을 합칠 곳
              <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}>
                <option value="">— 여행 고르기 —</option>
                {(trips || [])
                  .filter((t) => t.key !== detail.key)
                  .map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.title}
                      {t.start_date ? ` (${formatPeriod(t.start_date, t.end_date)})` : ''}
                    </option>
                  ))}
              </select>
            </label>
            <SaveBtn onClick={doMerge} disabled={!mergeTarget || mergeBusy}>
              {mergeBusy ? '합치는 중…' : '합치기'}
            </SaveBtn>
            <LinkBtn onClick={() => setMerging(false)} disabled={mergeBusy}>취소</LinkBtn>
            {formError && <ErrorText style={{ flexBasis: '100%', margin: 0 }}>{formError}</ErrorText>}
          </EditBox>
        )}

        {detail.merged_from?.length > 0 && (
          <MergedNote>
            함께 묶인 여행:
            {detail.merged_from.map((m) => (
              <span key={m.key} className="chip">
                {m.title}
                <button onClick={() => doUnmerge(m.key, m.title)} disabled={mergeBusy}>떼어 내기</button>
              </span>
            ))}
          </MergedNote>
        )}

        {editing && (
          <EditBox>
            <label className="title">
              보이는 이름
              <input
                value={form.title}
                placeholder={`${detail.key} 여행`}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label>
              출발
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </label>
            <label>
              도착
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </label>
            <SaveBtn onClick={saveEdit} disabled={saving}>{saving ? '저장 중…' : '저장'}</SaveBtn>
            <LinkBtn onClick={() => setEditing(false)} disabled={saving}>취소</LinkBtn>
            {formError && <ErrorText style={{ flexBasis: '100%', margin: 0 }}>{formError}</ErrorText>}
          </EditBox>
        )}

        <Summary>
          <div className="total">총 경비<strong>{won(detail.total)}</strong></div>
          {detail.refunded > 0 && (
            <div className="refund">환불·정산<strong>−{won(detail.refunded)}</strong></div>
          )}
          <div>내역<strong>{detail.count}건</strong></div>
        </Summary>

        <List>
          {detail.items.map((it) => {
            const refund = it.type === 'income';
            return (
              <Item key={it.id} $refund={refund} $excluded={!it.counted}>
                <span className="date">{shortDate(it.date)}</span>
                <span className="label">
                  {it.label}
                  {!it.counted && ' (계좌 이동 · 합계 제외)'}
                </span>
                <span className="amt">{refund ? '−' : ''}{won(it.amount)}</span>
                <span className="acct">{it.account_name}</span>
              </Item>
            );
          })}
        </List>

        {hasExcluded && (
          <Hint style={{ marginTop: 10 }}>
            흐리게 보이는 줄은 분류가 「이동」이라 쓴 돈이 아니므로 합계에 넣지 않았습니다.
          </Hint>
        )}
      </Wrap>
    );
  }

  // ---------- 여행 목록 ----------
  return (
    <Wrap>
      <Head>
        <h3>✈️ 여행 비용</h3>
        <Hint>
          어느 계좌든 메모를 <code>(여행) 퍼스: 항공권</code> 처럼 적으면 여기 여행별로 모입니다.
          카드를 누르면 그 여행에 들어간 경비가 모두 보입니다.
        </Hint>
      </Head>

      {error && <ErrorText>{error}</ErrorText>}

      {trips === null ? (
        <Hint>불러오는 중…</Hint>
      ) : trips.length === 0 ? (
        <Empty>
          아직 여행 경비가 없어요.
          <br />
          메모 앞에 <strong>(여행) 여행이름:</strong> 을 붙여 적어 보세요.
        </Empty>
      ) : (
        <Grid>
          {trips.map((t) => {
            const period = formatPeriod(t.start_date, t.end_date);
            return (
              <Card key={t.key} onClick={() => openTrip(t.key)}>
                <CardTitle>{t.title}</CardTitle>
                <Period $muted={!period}>{period ? `(${period})` : '기간 미정'}</Period>
                <Big>{won(t.total)}</Big>
                <Meta>
                  {t.count}건{t.last_paid ? ` · 마지막 결제 ${shortDate(t.last_paid)}` : ''}
                </Meta>
              </Card>
            );
          })}
        </Grid>
      )}
    </Wrap>
  );
}

export default TravelCosts;
